import type { ImageElement } from "@cva/shared";
import { Copy, PaintBucket, Palette } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { addImageColors, paletteFromUrl } from "../lib/palette";
import { cn, errorMessage } from "../lib/utils";
import { Swatch } from "./ColorPicker";
import { useEnv } from "./env";
import { useEditor, useEditorApi } from "./store";
import { Section } from "./ui";

/**
 * "Farbpalette aus Bild" in the image properties: the dominant colours of the
 * selected image. They show up in every colour picker ("Aus Bild") and can
 * become the page background.
 */
export function ImagePalette({ element }: { element: ImageElement }) {
  const env = useEnv();
  const store = useEditorApi();
  const readOnly = useEditor((s) => s.readOnly);
  const asset = env.assets.get(element.assetId);
  const [colors, setColors] = useState<string[] | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a new image starts over
  useEffect(() => {
    setColors(null);
    setPicked(null);
  }, [element.assetId]);

  if (!asset) return null;
  const extract = async () => {
    setLoading(true);
    try {
      const palette = await paletteFromUrl(asset.thumbnailUrl ?? asset.url, 6);
      const hexes = palette.map((entry) => entry.color);
      setColors(hexes);
      setPicked(hexes[0] ?? null);
      addImageColors(hexes);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section title="Farbpalette aus Bild">
      {colors === null ? (
        <button
          type="button"
          onClick={() => void extract()}
          disabled={loading}
          className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50 disabled:opacity-60"
        >
          <Palette className="size-4" />{" "}
          {loading ? "Farben werden ermittelt …" : "Farben ermitteln"}
        </button>
      ) : colors.length === 0 ? (
        <p className="text-xs text-zinc-500">Keine Farben gefunden (Bild ist transparent).</p>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-1.5">
            {colors.map((color) => (
              <Swatch
                key={color}
                color={color}
                title={color}
                onClick={() => setPicked(color)}
                className={cn(
                  "size-8",
                  picked === color && "ring-2 ring-brand-500 ring-offset-1 ring-inset",
                )}
              />
            ))}
          </div>
          {picked ? (
            <div className="flex gap-1.5">
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => {
                    const pageIndex = store.getState().pageIndex;
                    store.getState().update((draft) => {
                      const page = draft.pages[pageIndex];
                      if (!page) return;
                      page.background = picked;
                      delete page.brand;
                    });
                  }}
                  className="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded-md border border-zinc-200 text-xs hover:bg-zinc-50"
                >
                  <PaintBucket className="size-3.5" /> Als Hintergrund
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(picked);
                  toast.success(`${picked} kopiert`);
                }}
                className="inline-flex h-7 items-center justify-center gap-1 rounded-md border border-zinc-200 px-2 text-xs hover:bg-zinc-50"
              >
                <Copy className="size-3.5" /> {picked}
              </button>
            </div>
          ) : null}
          <p className="text-[11px] text-zinc-500">
            Die Farben stehen in jeder Farbauswahl unter „Aus Bild“ bereit.
          </p>
        </div>
      )}
    </Section>
  );
}
